import { FieldAttributes, useField, useFormikContext } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { Form, OverlayTrigger } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { FieldLabel } from '../index';
import FieldTooltip from './field-tooltip';

import './field-boolean-tristate.scss';

/** Tri-state value: true = Yes, false = No, null = Do not apply */
export type TriStateValue = boolean | null;

const VALUE_NA = 'na';

type Variant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'dark';

type Props = {
  readonly [key: string]: unknown;
  readonly as?: React.ElementType<unknown>;
  readonly label: string;
  /** Optional explanatory text shown in an inline expandable panel below the label */
  readonly helpText?: string;
  /** Optional label for the help trigger, e.g. t('Τι σημαίνει αυτό;') */
  readonly helpTriggerLabel?: string;
  readonly onLabel?: string;
  readonly offLabel?: string;
  readonly naLabel?: string;
  readonly variantYes?: Variant;
  readonly variantNo?: Variant;
  readonly variantNa?: Variant;
  readonly disabled?: boolean;
  readonly onChange?: (value: TriStateValue, fieldName: string) => void;
} & FieldAttributes<unknown>;

const FieldBooleanTriState = ({
  as = 'div',
  name,
  label,
  helpText,
  helpTriggerLabel,
  onLabel,
  offLabel,
  naLabel,
  variantYes = 'primary',
  variantNo = 'warning',
  variantNa = 'secondary',
  disabled = false,
  onChange = undefined,
}: Props) => {
  const { t, i18n } = useTranslation();
  const onL = onLabel ?? (i18n.language === 'gr' ? t('Ναι') : t('Yes'));
  const offL = offLabel ?? (i18n.language === 'gr' ? t('Όχι') : t('No'));
  const naL = naLabel ?? (i18n.language === 'gr' ? t('Δεν εφαρμόζεται') : t('Do not apply'));

  const [{ value }, { initialValue }] = useField<TriStateValue>(name);
  const { setFieldValue, setFieldTouched } = useFormikContext<Record<string, unknown>>();

  const setValueAndTouched = useCallback(
    async (val: TriStateValue, touched: boolean) => {
      await setFieldValue(name, val, true);
      await setFieldTouched(name, touched);
    },
    [name, setFieldTouched, setFieldValue],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const next: TriStateValue = raw === VALUE_NA ? null : raw === 'true';
      setValueAndTouched(next, true);
      onChange?.(next, name);
    },
    [name, onChange, setValueAndTouched],
  );

  const changed = value !== initialValue;
  const tooltipText = value === null || value === undefined ? naL : value ? onL : offL;

  return useMemo(
    () => (
      <Form.Group
        as={as as React.ElementType}
        controlId={'formGroup_' + name}
        className="mb-2 field-boolean-tristate"
      >
        <FieldLabel text={label} changed={changed} helpText={helpText} />
        <OverlayTrigger
          delay={{ show: 1000, hide: 200 }}
          overlay={<FieldTooltip text={name} value={tooltipText} />}
          placement="top"
        >
          <div className="d-flex flex-wrap gap-3">
            <div
              className={`field-boolean-tristate__option field-boolean-tristate__option--${variantYes}`}
            >
              <Form.Check
                type="radio"
                id={`${name}_yes`}
                name={name}
                value="true"
                label={onL}
                checked={value === true}
                onChange={handleChange}
                disabled={disabled}
              />
            </div>
            <div
              className={`field-boolean-tristate__option field-boolean-tristate__option--${variantNo}`}
            >
              <Form.Check
                type="radio"
                id={`${name}_no`}
                name={name}
                value="false"
                label={offL}
                checked={value === false}
                onChange={handleChange}
                disabled={disabled}
              />
            </div>
            <div
              className={`field-boolean-tristate__option field-boolean-tristate__option--${variantNa}`}
            >
              <Form.Check
                type="radio"
                id={`${name}_na`}
                name={name}
                value={VALUE_NA}
                label={naL}
                checked={value === null || value === undefined}
                onChange={handleChange}
                disabled={disabled}
              />
            </div>
          </div>
        </OverlayTrigger>
      </Form.Group>
    ),
    [
      as,
      name,
      label,
      helpText,
      helpTriggerLabel,
      changed,
      value,
      onL,
      offL,
      naL,
      tooltipText,
      variantYes,
      variantNo,
      variantNa,
      handleChange,
      disabled,
    ],
  );
};

export default FieldBooleanTriState;
